import React from 'react';
import Logger from '../../util/Logger';
import { connect } from 'react-redux';
import './Authentication.css';
import { AuthMethod } from '../../models/AuthMethod';
import User from '../../models/User';
import { updateAuthMethod } from '../../actions/authentication';
import { showDialog } from '../../actions/dialog';
import Tenant from '../../models/Tenant';
import { API_POPIN_PREFIX, PAGE_LINK_AUTH_LEARN_MORE } from '../../constants';
import { PulseLoader } from 'react-spinners';
import Dropdown from '../shared/Dropdown';
import { handleErrorWithDialog } from '../../util/Errors';
import { EnterpriseSettings } from '../../analytics/EnterpriseSettings';
import { NavigationUtils } from '../../util/NavigationUtils';

class SamlSettings extends React.Component {

  constructor (props) {
    super(props);

    const { user } = this.props;
    if (!user) {
      Logger.assert('SamlSettings component cannot be used without a User object');
    }

    this.state = {
      samlMethod: getSamlMethod(user),
      samlUrl: Tenant.getSamlIdpMetadataUrl(User.getTenant(user)),
      samlFile: null,
      samlEditing: false
    };
  }

  handleAuthLearnMoreClicked = ($event) => {
    $event.preventDefault();
    NavigationUtils.openTab(PAGE_LINK_AUTH_LEARN_MORE);
  };

  /**
   * The actual network call to enable SAML
   *
   * All params call be null
   * @param enableSaml
   * @param samlUrl
   * @param samlFile
   */
  saveSamlAuthPayload = (enableSaml, samlUrl, samlFile) => {
    const { user, updateAuthMethod } = this.props;
    if (user) {
      updateAuthMethod(Tenant.getId(User.getTenant(user)), enableSaml, samlUrl, samlFile);
    }
  };

  /**
   * This is called when a SAML method is picked, either File or URL
   * It does not mean that SAML is enabled or disabled.
   * @param item
   */
  handleSamlMethodSelected = (item) => {
    this.setState({ samlMethod: item });
    EnterpriseSettings.settingsFromMetadataURL(item.id);
    this.handleSamlUrlReset();
    this.handleSamlFileReset();
  };

  saveSamlIdpUrl = () => {
    const { samlUrl } = this.state;
    this.saveSamlAuthPayload(null, samlUrl, null);
    this.handleSamlUrlReset();
    this.handleSamlFileReset();
  };

  saveSamlMetadataFile = () => {
    const { samlFile } = this.state;
    this.saveSamlAuthPayload(null, null, samlFile);
    this.handleSamlUrlReset();
    this.handleSamlFileReset();
  };

  handleSamlUrlChanged = ($event) => {
    this.setState({
      samlUrl: $event.target.value || '',
      samlEditing: true
    });
  };

  handleSamlUrlReset = () => {
    const { user } = this.props;
    this.setState({
      samlUrl: Tenant.getSamlIdpMetadataUrl(User.getTenant(user)),
      samlEditing: false
    });
  };

  handleSamlFileChanged = ($event) => {
    const files = $event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      this.setState({
        samlFile: file,
        samlEditing: true
      }, this.saveSamlMetadataFile);
    }
  };

  handleSamlFileReset = () => {
    this.setState({
      samlFile: null,
      samlEditing: false
    });
  };

  handleSamlUrlSaveClicked = ($event) => {
    $event.preventDefault();
    this.saveSamlIdpUrl();
  };

  handleSamlEditingBegin = () => {
    const { authMethod, showDialog } = this.props;
    if (authMethod !== AuthMethod.LOGIN_WITH_SSO) {
      this.setState({ samlEditing: true });
    } else {
      showDialog({
        title: 'Are you sure?',
        body: (
          <div>
            Since you are already on SSO, this change will take effect
            <br />immediately after you click Ok.
          </div>
        ),
        ok: 'Ok',
        onOk: () => {
          this.setState({ samlEditing: true });
        },
        cancel: 'Cancel'
      });
    }
  };

  enableSaml = () => {
    const { authMethod, showDialog } = this.props;
    if (authMethod !== AuthMethod.LOGIN_WITH_SSO) {
      showDialog({
        title: 'You are about to switch to SAML2 Single Sign-on Authentication',
        body: (
          <div>
            Users will be required to use SAML authentication to log into POPin.
            <br /><span
            className="link"
            onClick={ this.handleAuthLearnMoreClicked }>Learn more</span>
          </div>
        ),
        ok: 'Ok',
        cancel: 'Cancel',
        onOk: () => {
          this.saveSamlAuthPayload(AuthMethod.LOGIN_WITH_SSO, null, null);
          this.handleSamlUrlReset();
          this.handleSamlFileReset();
        }
      });
    }
  };

  /**
   * When the SAML verify button is clicked
   *
   * @param $event
   */
  handleVerifySamlClicked = ($event) => {
    $event.preventDefault();
    const { verifyUrl } = this.props;
    if (verifyUrl) {
      NavigationUtils.openTab(verifyUrl);
    }
  };

  /**
   * When SAML enabled is clicked
   *
   * @param $event
   */
  handleEnableSamlClicked = ($event) => {
    $event.preventDefault();
    this.enableSaml();
  };

  componentDidUpdate (prevProps) {
    const { samlUrl, verifyError } = this.props;
    handleErrorWithDialog(verifyError, prevProps.verifyError,
      'Unable to verify SAML configuration');

    // The SAML url has updated, replace whatever state says with the server's URL
    if (samlUrl !== prevProps.samlUrl) {
      this.setState({ samlUrl });
    }
  }

  render () {
    const { user, authUpdating, verifyUpdating, authMethod, clientAuthMethod } = this.props;
    const { samlMethod, samlUrl, samlEditing, samlFile } = this.state;
    const hasSamlSettings = (Tenant.getSamlIdpMetadataUrl(User.getTenant(user)) ||
      Tenant.getSamlIdpXmlMetadata(User.getTenant(user)));

    // Show this section only if we either are using SSO, or if we have selected SSO sso and have
    // existing prereq settings.
    if (authMethod === AuthMethod.LOGIN_WITH_SSO || clientAuthMethod ===
      AuthMethod.LOGIN_WITH_SSO) {
      return (
        <div className="col no-gutters fix-ie">
          <SamlEditSettings
            user={ user }
            clientAuthMethod={ clientAuthMethod }
            authMethod={ authMethod }
            samlMethod={ samlMethod }
            samlUrl={ samlUrl }
            samlFile={ samlFile }
            samlEditing={ samlEditing }
            authUpdating={ authUpdating }
            onUrlChanged={ this.handleSamlUrlChanged }
            onFileChanged={ this.handleSamlFileChanged }
            onUrlSaved={ this.handleSamlUrlSaveClicked }
            onDropdownSelected={ this.handleSamlMethodSelected }
            onEditClicked={ this.handleSamlEditingBegin }
          />
          { hasSamlSettings && (
            <div className="col no-gutters">
              <div className="h-divider my-3" />
              <div className="col no-gutters">

                <div className="col mt-3">
                  <IdpSettings user={ user } />
                </div>

                <div className="col mt-3">
                  <SpSettings user={ user } />
                </div>

              </div>
              <div className="h-divider my-3" />
              <div className="row">
                <div className="col-8" />
                <div className="col-4 clearfix">
                  { authMethod !== AuthMethod.LOGIN_WITH_SSO && <div className="row float-right">
                    <button
                      type="button"
                      className="btn pop-btn-secondary mr-3"
                      onClick={ this.handleVerifySamlClicked }>
                      { verifyUpdating
                        ? <PulseLoader color="#FFFFFF" size={ 10 } />
                        : 'Verify'
                      }
                    </button>
                    <button
                      type="button"
                      className="btn pop-btn"
                      onClick={ this.handleEnableSamlClicked }>
                      Enable
                    </button>
                  </div>
                  }
                </div>
              </div>
            </div>
          ) }
        </div>
      );
    } else {
      return null;
    }
  }
}

const SamlEditSettings = ({
  user,
  clientAuthMethod,
  authMethod,
  samlMethod,
  samlUrl,
  samlFile,
  samlEditing,
  authUpdating,
  onUrlChanged,
  onFileChanged,
  onUrlSaved,
  onDropdownSelected,
  onEditClicked
}) => {
  const authMethodIsSaml = (clientAuthMethod
    ? clientAuthMethod === AuthMethod.LOGIN_WITH_SSO
    : authMethod === AuthMethod.LOGIN_WITH_SSO);
  const samlMethodIsUrl = (samlMethod.id === SamlMethods.URL);

  let samlFileUploadRef = null;
  const setSamlFileUploadRef = (element) => {
    samlFileUploadRef = element;
  };

  const handleSamlBrowseClicked = ($event) => {
    $event.preventDefault();
    if (samlFileUploadRef) {
      samlFileUploadRef.click();
    }
  };

  const handleOnSamlSubmit = ($event) => {
    if (samlMethodIsUrl) {
      return onUrlSaved($event);
    } else {
      return handleSamlBrowseClicked($event);
    }
  };

  const handleEditClicked = ($event) => {
    if (authMethodIsSaml) {
      onEditClicked($event);
    }
  };

  const doNothing = ($event) => {$event.preventDefault();};

  const idpMetadata = Tenant.getSamlIdpXmlMetadata(User.getTenant(user));
  const samlColor = authMethodIsSaml ? 'saml-enabled' : 'saml-disabled';
  const viewingSamlLayout = (
    <div className="row no-gutters">
      <div className={ `font-weight-bold font-size-md text-truncate mr-3 ${samlColor}` }>
        { samlMethodIsUrl ? samlUrl : 'Replace metadata file' }
      </div>
      <i
        className={ `material-icons ${samlColor} ${authMethodIsSaml ? 'clickable' : ''}` }
        onClick={ handleEditClicked }>edit</i>
    </div>
  );

  // If we are editing, display edit mode
  // Else if we aren't editing but don't have content, display edit mode
  const editing = samlEditing
    ? true
    : (samlMethodIsUrl && samlUrl)
      ? false
      : (!(!samlMethodIsUrl && idpMetadata));

  const editingSamlLayout = (
    <div className="row">
      <form
        className="col-lg-9"
        name="samlUrl"
        onSubmit={ handleOnSamlSubmit }>
        <input
          type="text"
          disabled={ !authMethodIsSaml }
          className="pop-input col no-gutters pop-form-control"
          value={ samlMethodIsUrl ? samlUrl : samlFile ? samlFile.name : '' }
          onChange={ samlMethodIsUrl ? onUrlChanged : doNothing } />
      </form>
      <div className="col-lg-2 clearfix">
        <input
          type="file"
          id="samlFileUpload"
          ref={ setSamlFileUploadRef }
          accept="text/xml"
          className="d-none"
          onChange={ onFileChanged } />
        <button
          type="button"
          className="btn pop-btn float-right"
          disabled={ !authMethodIsSaml || (samlMethodIsUrl && !samlUrl) }
          onClick={ handleOnSamlSubmit }>
          { authUpdating
            ? <PulseLoader color="#FFFFFF" size={ 10 } />
            : `${samlMethodIsUrl ? 'Save' : 'Browse'}`
          }
        </button>
      </div>
      <div className="col-lg-1" />
    </div>
  );

  if (authMethod === AuthMethod.LOGIN_WITH_SSO || clientAuthMethod === AuthMethod.LOGIN_WITH_SSO) {
    return (
      <div className="col no-gutters mt-1">
        <Dropdown
          id="saml-dropdown"
          disabled={ !authMethodIsSaml }
          text={ samlMethod.text }
          itemList={ getSamlMethods() }
          onSelected={ onDropdownSelected }
        />

        { editing ? editingSamlLayout : viewingSamlLayout }
      </div>
    );
  } else {
    return null;
  }
};

const getSamlMethods = () => {
  return [
    {
      id: SamlMethods.URL,
      text: 'Settings from Metadata URL'
    },
    {
      id: SamlMethods.FILE,
      text: 'Settings from Metadata File'
    }
  ];
};

const SamlMethods = {
  FILE: 'file',
  URL: 'url'
};

const getSamlMethod = (user) => {
  const methods = getSamlMethods();
  if (Tenant.getSamlIdpXmlMetadata(User.getTenant(user))) {
    return methods[1];
  } else {
    return methods[0];
  }
};

class CollapseComponent extends React.Component {

  toggleCollapsed = () => {
    const oldState = this.state.collapsed || false;
    this.setState({
      collapsed: !oldState
    });
  };

  setCollapsed = (open) => {
    this.setState({
      collapsed: open
    });
  };

  constructor (props) {
    super(props);

    this.state = {
      collapsed: true
    };
  }

  render () {
    Logger.assert('You must override the render function in your CollapseComponent subclass');
    return null;
  }
}

class MetadataXmlBlock extends CollapseComponent {

  renderCollapsingSection = () => {
    const { xml } = this.props;
    return (
      <div className="row mt-3">
        { xml
          ? (
            <pre className="pre-scrollable fix-ie font-weight-bold w-100">
                <code>
                  { xml }
                </code>
              </pre>
          )
          : 'SSO is not set up for your Organization' }
      </div>
    );
  };

  handleDownloadClicked = ($event) => {
    $event.preventDefault();

    const { xml, url } = this.props;
    if (xml) {
      NavigationUtils.openTab(url);
    } else {
      this.setCollapsed(false);
    }
  };

  render () {
    const { collapsed } = this.state;
    return (
      <div className="col">

        <div className="mt-1 row">
          <div className="font-weight-bold mr-3 text-center">
            Metadata XML
          </div>

          <div className="link mr-3 text-center clickable" onClick={ this.toggleCollapsed }>
            { collapsed ? 'View' : 'Hide' }
          </div>

          <div className="link text-center clickable" onClick={ this.handleDownloadClicked }>
            Download
          </div>

        </div>

        { collapsed || this.renderCollapsingSection() }

      </div>
    );
  }
}

class IdpSettings extends React.Component {

  renderCollapsingSection = () => {
    const { user } = this.props;
    const xml = Tenant.getSamlIdpXmlMetadata(User.getTenant(user));
    const tenantId = Tenant.getId(User.getTenant(user));
    const url = `${API_POPIN_PREFIX}/admin/tenants/${tenantId}/saml/idp/export`;
    return (
      <div>
        <MetadataXmlBlock
          xml={ xml }
          url={ url } />
      </div>
    );
  };

  render () {
    return (
      <div className="row">
        <div className="font-weight-bold d-inline-block clickable col-2 text-truncate">
          <span>IdP Settings</span>
        </div>

        <div className="col-10">
          { this.renderCollapsingSection() }
        </div>
      </div>
    );
  }
}

class SpSettings extends React.Component {

  renderCollapsingSection = () => {
    const { user } = this.props;
    const tenant = User.getTenant(user);
    const attributes = Tenant.getSamlSpRequiredUserAttributes(tenant);
    const xml = Tenant.getSamlSpXmlMetadata(tenant);
    const noSso = `SSO is not set up for your Organization`;
    const tenantId = Tenant.getId(tenant);
    const url = `${API_POPIN_PREFIX}/admin/tenants/${tenantId}/saml/sp/export`;
    return (
      <div>
        <MetadataXmlBlock xml={ xml } url={ url } />

        <h2 className="mt-3">SSO URL</h2>
        <div className="font-weight-bold">{ Tenant.getSamlSpSsoUrl(tenant) || noSso }</div>

        <h2 className="mt-3">Assertion Consumer Services (ACS) URL</h2>
        <div className="font-weight-bold">{ Tenant.getSamlSpAcsUrl(tenant) || noSso }</div>

        <h2 className="mt-3">Name ID Format</h2>
        <div className="font-weight-bold">{ Tenant.getSamlSpNameIdFormat(tenant) || noSso }</div>

        <h2 className="mt-3">Required User Attributes</h2>
        {
          attributes && attributes.length > 0
            ? attributes.map(a => (
              <div
                key={ a }
                className="ml-3 font-weight-bold">
                - { a }
              </div>)
            )
            : <div className="font-weight-bold">SSO is not set up for your Organization</div> }
      </div>
    );
  };

  render () {
    return (
      <div className="row">
        <div className="font-weight-bold d-inline-block clickable col-2 text-truncate">
          <span>SP Settings</span>
        </div>

        <div className="col-10">
          { this.renderCollapsingSection() }
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  const user = state.user.user;
  return {
    user: user,
    authMethod: Tenant.getAuthMethod(User.getTenant(user)),
    verifyUrl: Tenant.getSamlVerifyUrl(User.getTenant(user)),
    samlUrl: Tenant.getSamlIdpMetadataUrl(User.getTenant(user)),
    authUpdating: state.authentication.loading,
    verifyUpdating: state.authentication.verifyLoading,
    verifyError: state.authentication.verifyError
  };
};

export default connect(mapStateToProps, { updateAuthMethod, showDialog })(SamlSettings);
